// Branch Overview drill-down dashboard (iter-214).
// Rendered when the operator clicks a branch card on the Brand
// Overview page. Rolls up appointments, revenue, top services, top
// customers, new customers and % schedule-filled for a Bali date
// range chosen by the operator. Every value below is server-computed
// via /api/reports/brand/branch-overview/{bid} so the Total Revenue
// number always matches the standard Sales Report math.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Star, User as UserIcon, RefreshCw, Download } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from "recharts";
import StaffShell from "@/components/layout/StaffShell";
import { staffApi, formatApiError, formatBranchMoney } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
// Iter-312 · BI Isolation — all metric math + date helpers live in
// ReportingEngine.jsx. Snapshot capture + filename helpers live in
// NotificationEngine.jsx. See SYSTEM_RULES.md.
import {
  todayBali,
  rangeForPreset,
  PRESETS,
  DAY_COLOR_MAP,
  computeAppointmentsByDay,
  sortServices,
  sortCareTeam,
  sortCustomers,
  deriveCancellationLabel,
  deriveCancellationReason,
} from "@/lib/ReportingEngine";
import { useDashboardSnapshot, formatSnapshotDateLabel } from "@/lib/NotificationEngine";

// Miniature stat card matching the Mindbody-style "Big number + Your
// Average" cluster. The chevron colour tints red when we're under the
// average and green when we beat it — matches the reference image.
// `caption` renders a tiny grey helper line at the bottom explaining
// how the number is computed (iter-226).
function StatCard({ label, value, average, testId, formatter = (v) => v.toLocaleString("en-US"), caption }) {
  const beat = average !== undefined && average !== null && value >= average;
  return (
    <Card className="rounded-lg" data-testid={testId}>
      <CardContent className="p-5">
        <p className="tracking-overline text-[10px] text-muted-foreground">{label}</p>
        <div className="flex items-baseline gap-3 mt-2">
          <p className="text-5xl font-semibold text-sky-500 leading-none" data-testid={`${testId}-value`}>
            {formatter(value ?? 0)}
          </p>
          {average !== undefined && average !== null && (
            <>
              <span
                aria-hidden
                className={`inline-block w-0 h-0 border-x-[6px] border-x-transparent ${
                  beat ? "border-b-[9px] border-b-emerald-500" : "border-t-[9px] border-t-rose-500"
                }`}
              />
              <div className="text-xs text-muted-foreground">
                <div>Your</div>
                <div>Average: <span className="font-medium text-foreground">{formatter(average)}</span></div>
              </div>
            </>
          )}
        </div>
        {caption && (
          <p
            className="mt-3 text-[10px] leading-tight text-muted-foreground/70"
            data-testid={`${testId}-caption`}
          >
            {caption}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

// Reusable "Quantity | Revenue" toggle used by both the Top Services
// and the Most Performing Care Team cards. Keeps the active option
// bold + primary-tinted so the operator instantly sees which sort
// mode is driving the list. Kept tiny to hug the card header.
function SortToggle({ mode, onChange, testId }) {
  return (
    <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider" data-testid={testId}>
      <button
        type="button"
        onClick={() => onChange("quantity")}
        className={`px-1 rounded transition-colors ${mode === "quantity" ? "text-primary font-semibold underline underline-offset-4" : "text-muted-foreground hover:text-foreground"}`}
        data-testid={`${testId}-quantity`}
      >Quantity</button>
      <span className="text-muted-foreground">|</span>
      <button
        type="button"
        onClick={() => onChange("revenue")}
        className={`px-1 rounded transition-colors ${mode === "revenue" ? "text-primary font-semibold underline underline-offset-4" : "text-muted-foreground hover:text-foreground"}`}
        data-testid={`${testId}-revenue`}
      >Revenue</button>
    </div>
  );
}

export default function BranchOverview() {
  const { bid } = useParams();
  const navigate = useNavigate();
  const [branch, setBranch] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [preset, setPreset] = useState("this_month");
  const [customFrom, setCustomFrom] = useState(todayBali());
  const [customTo, setCustomTo] = useState(todayBali());
  const [svcSort, setSvcSort] = useState("revenue");
  const [careSort, setCareSort] = useState("revenue");
  // Top Customers sort mode (iter-265) — "spend" (default, matches
  // legacy behaviour) or "visits". The backend hands us both numbers
  // so flipping the toggle is instant with no refetch.
  const [custSort, setCustSort] = useState("spend");
  // Iter-266 — Dynamic layout for daily views. When the operator
  // picks Today or Yesterday, we swap the trend cards (Top Services
  // ranked list + Day-of-Week pie) for the two static summaries the
  // manager cares about at end-of-shift and move Total Revenue to
  // the very top. Every other preset keeps the trend layout.
  const isDaily = preset === "today" || preset === "yesterday";

  useEffect(() => {
    staffApi.get("/branches").then((r) => setBranch((r.data || []).find((b) => b.id === bid) || null));
  }, [bid]);

  const range = useMemo(() => rangeForPreset(preset, customFrom, customTo), [preset, customFrom, customTo]);

  const load = useCallback(async () => {
    if (!bid) return;
    setLoading(true);
    // Iter-226 — the Refresh button must actively CLEAR stale numbers
    // before the new fetch resolves so the operator can visually
    // confirm the reload isn't just re-rendering cached state.
    setData(null);
    try {
      const r = await staffApi.get(`/reports/brand/branch-overview/${bid}`, { params: range });
      setData(r.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [bid, range]);

  useEffect(() => { load(); }, [load]);

  const money = (v) => formatBranchMoney(branch, v ?? 0);

  // Iter-312 · pie + ranked-list math delegated to ReportingEngine.
  const pieData = useMemo(() => computeAppointmentsByDay(data?.appointments_by_day), [data]);
  const sortedServices = useMemo(() => sortServices(data?.top_services, svcSort), [data, svcSort]);
  const sortedCareTeam = useMemo(() => sortCareTeam(data?.top_care_team, careSort), [data, careSort]);
  const sortedCustomers = useMemo(() => sortCustomers(data?.top_customers, custSort, 10), [data, custSort]);

  const dateLabel = formatSnapshotDateLabel(range.date_from, range.date_to);
  const snapshotLabel = branch?.name ? `${branch.name} Overview` : "Branch Overview";
  const { downloadRef, downloadPng, capturing } = useDashboardSnapshot({
    label: snapshotLabel,
    dateFrom: range.date_from,
    dateTo: range.date_to,
  });

  return (
    <StaffShell context="branch" branchName={branch?.name}>
      <div className="max-w-6xl mx-auto space-y-5" ref={downloadRef} data-testid="branch-overview-snapshot-root">
        {/* Iter-308 · Snapshot-only report banner so the exported PNG
            (and any emailed screenshot) leads with the actual Bali
            calendar date instead of just the preset label. Hidden
            from normal on-screen use by the `snapshot-only` class. */}
        <div
          className="snapshot-only text-center px-4 py-3 border border-border rounded-lg bg-muted/30"
          data-testid="branch-overview-snapshot-header"
        >
          <p className="tracking-overline text-[10px] text-muted-foreground">COCOON MEDICAL SPA</p>
          <p className="text-xl font-semibold mt-1">
            {branch?.name ? `${branch.name} · Branch Overview` : "Branch Overview"}
          </p>
          {dateLabel && (
            <p className="text-sm text-muted-foreground mt-1" data-testid="branch-overview-snapshot-date">
              {dateLabel}
            </p>
          )}
        </div>
        <div className="flex items-start flex-wrap gap-3">
          <Button asChild variant="outline" size="sm" className="gap-1 h-9" data-testid="branch-overview-back">
            <Link to="/staff/brand"><ArrowLeft className="w-4 h-4" /> Back to Brand</Link>
          </Button>
          <div className="min-w-0 flex-1">
            <h1 className="text-2xl font-semibold leading-tight" data-testid="branch-overview-title">
              {branch?.name ? `${branch.name} · Branch Overview` : "Branch Overview"}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Drill-down snapshot — appointments, top services, top guests, and net sales for the selected period.
            </p>
            {dateLabel && (
              <p
                className="text-xs font-medium text-sky-600 mt-1"
                data-testid="branch-overview-date-label"
              >
                {dateLabel}
              </p>
            )}
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={downloadPng}
            disabled={capturing || loading}
            className="gap-1 h-9 shrink-0"
            data-testid="branch-overview-download-png"
            data-html2canvas-ignore="true"
          >
            <Download className={`w-4 h-4 ${capturing ? "animate-pulse" : ""}`} />
            {capturing ? "Capturing…" : "Download Dashboard (PNG)"}
          </Button>
        </div>

        {/* Date range picker */}
        <Card className="rounded-lg">
          <CardContent className="p-3 flex items-center gap-2 flex-wrap">
            <span className="text-xs uppercase tracking-wider text-muted-foreground mr-1">Date range</span>
            <Select value={preset} onValueChange={setPreset}>
              <SelectTrigger className="w-48" data-testid="branch-overview-preset"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PRESETS.map((p) => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
            {preset === "custom" && (
              <>
                <Input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} className="w-40" data-testid="branch-overview-custom-from" />
                <span className="text-xs text-muted-foreground">to</span>
                <Input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} className="w-40" data-testid="branch-overview-custom-to" />
              </>
            )}
            <Button size="sm" variant="outline" onClick={load} disabled={loading} className="gap-1 ml-auto" data-testid="branch-overview-refresh">
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
            </Button>
          </CardContent>
        </Card>

        {/* Iter-266 — Daily Mode: Total Revenue is the FIRST number
            the manager sees at end-of-shift.
            Iter-282 — Standard Mode (any preset OTHER than Today /
            Yesterday) now promotes Total Revenue to Row 1 as well.
            Iter-283 — Standard-Mode heroes render side-by-side in a
            compact two-column row (Total Revenue | New Guests) so
            the manager sees both signals without scrolling. Daily
            Mode keeps the single full-width hero (the New Guests
            slot is inside its dedicated stat pair below). */}
        {isDaily ? (
          <Card className="rounded-lg" data-testid="total-revenue-card-top">
            <CardContent className="p-5">
              <p className="tracking-overline text-[10px] text-muted-foreground">TOTAL REVENUE</p>
              <p className="text-6xl font-semibold text-sky-500 leading-none mt-3" data-testid="total-revenue-value">
                {money(data?.total_revenue ?? 0)}
              </p>
              {data && data.revenue_refunds > 0 && (
                <p className="text-[11px] text-muted-foreground mt-2">
                  Gross {money(data.revenue_gross)} · Refunds {money(data.revenue_refunds)}
                </p>
              )}
              <p
                className="mt-3 text-[10px] leading-tight text-muted-foreground/70"
                data-testid="total-revenue-caption"
              >
                Net sales for {preset === "today" ? "today" : "yesterday"} (payments minus refunds). Matches the Sales report.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 gap-4" data-testid="hero-row">
            <Card className="rounded-lg" data-testid="total-revenue-card-top">
              <CardContent className="p-5">
                <p className="tracking-overline text-[10px] text-muted-foreground">TOTAL REVENUE</p>
                <p className="text-5xl font-semibold text-sky-500 leading-none mt-3" data-testid="total-revenue-value">
                  {money(data?.total_revenue ?? 0)}
                </p>
                {data && data.revenue_refunds > 0 && (
                  <p className="text-[11px] text-muted-foreground mt-2">
                    Gross {money(data.revenue_gross)} · Refunds {money(data.revenue_refunds)}
                  </p>
                )}
                <p
                  className="mt-3 text-[10px] leading-tight text-muted-foreground/70"
                  data-testid="total-revenue-caption"
                >
                  Net sales for the selected range (payments minus refunds). Matches the Sales report math.
                </p>
              </CardContent>
            </Card>
            <Card className="rounded-lg" data-testid="new-guests-card-top">
              <CardContent className="p-5">
                <p className="tracking-overline text-[10px] text-muted-foreground">NEW GUESTS</p>
                <div className="flex items-baseline gap-3 mt-3">
                  <p className="text-5xl font-semibold text-sky-500 leading-none" data-testid="new-guests-value">
                    {(data?.new_customers ?? 0).toLocaleString("en-US")}
                  </p>
                  {data?.new_customers_avg !== undefined && data?.new_customers_avg !== null && (
                    <>
                      <span
                        aria-hidden
                        className={`inline-block w-0 h-0 border-x-[6px] border-x-transparent ${
                          (data?.new_customers ?? 0) >= data.new_customers_avg
                            ? "border-b-[9px] border-b-emerald-500"
                            : "border-t-[9px] border-t-rose-500"
                        }`}
                      />
                      <div className="text-xs text-muted-foreground">
                        <div>Your</div>
                        <div>
                          Average:{" "}
                          <span className="font-medium text-foreground">
                            {(data.new_customers_avg ?? 0).toLocaleString("en-US")}
                          </span>
                        </div>
                      </div>
                    </>
                  )}
                </div>
                <p
                  className="mt-3 text-[10px] leading-tight text-muted-foreground/70"
                  data-testid="new-guests-caption"
                >
                  Guests whose first-ever appointment at this branch fell in the selected range. &lsquo;Your Average&rsquo; is scaled from the last 30 days of activity at this branch.
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Top row: two big-number stat cards.
            Iter-275 · Daily Mode swaps to `Guests Treated | New Guests`
            (unique customer_ids + first-visit count) so the manager
            reads the guest picture at a glance.
            Iter-282 · Standard Mode replaces the legacy `Appointments
            Booked` slot with `Guests Treated` so the top-of-page metric
            trio (Revenue → New Guests → Guests Treated) all speak the
            same "unique visitor" language, and pairs it with the
            existing `% Schedule Booked` utilisation card. */}
        {isDaily ? (
          <div className="grid md:grid-cols-2 gap-4">
            <StatCard
              label="GUESTS TREATED"
              value={data?.guests_treated ?? 0}
              average={data?.guests_treated_avg}
              testId="stat-guests-treated"
              caption="Unique guests seen on this day — one visitor with 5 services still counts as 1. 'Your Average' is scaled from the last 30 days of activity at this branch."
            />
            <StatCard
              label="NEW GUESTS"
              value={data?.new_customers ?? 0}
              average={data?.new_customers_avg}
              testId="stat-new-guests"
              caption="Guests whose first-ever appointment at this branch fell on this day."
            />
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            <StatCard
              label="GUESTS TREATED"
              value={data?.guests_treated ?? 0}
              average={data?.guests_treated_avg}
              testId="stat-guests-treated"
              caption="Unique guests seen in the selected range — one visitor with 5 services still counts as 1. 'Your Average' is scaled from the last 30 days of activity at this branch."
            />
            <StatCard
              label="% SCHEDULE BOOKED"
              value={data?.pct_schedule_booked ?? 0}
              average={null}
              testId="stat-schedule-pct"
              formatter={(v) => `${Math.round(Number(v) || 0)}%`}
              caption="Based on total treated minutes vs. staff duty hours (weekly schedule of every active practitioner, excluding lunch)."
            />
          </div>
        )}

        {/* Middle row (Trend Mode): services + pie
            Middle row (Daily Mode): services summary + packages summary */}
        {isDaily ? (
          <div className="grid md:grid-cols-2 gap-4">
            <Card className="rounded-lg" data-testid="services-summary-card">
              <CardContent className="p-5">
                <div className="flex items-baseline justify-between mb-3">
                  <h2 className="tracking-overline text-[10px] text-muted-foreground">SERVICES SUMMARY</h2>
                  <span className="text-[10px] text-muted-foreground uppercase tracking-wider">Qty · Revenue</span>
                </div>
                {loading ? (
                  <p className="text-xs text-muted-foreground py-4">Loading…</p>
                ) : !(data?.services_summary || []).length ? (
                  <p className="text-xs text-muted-foreground py-4" data-testid="services-summary-empty">
                    No individual services closed on this day.
                  </p>
                ) : (
                  <ul className="divide-y divide-border/50">
                    {data.services_summary.map((s) => (
                      <li
                        key={s.service_id || s.service_name}
                        className="flex items-center gap-3 py-1.5"
                        data-testid={`service-summary-${s.service_id || s.service_name}`}
                      >
                        <span className="uppercase text-xs font-medium truncate flex-1">{s.service_name}</span>
                        <span className="text-[11px] text-muted-foreground tabular-nums w-10 text-right">×{s.quantity}</span>
                        <span
                          className="text-xs text-muted-foreground whitespace-nowrap tabular-nums"
                          data-testid={`service-summary-${s.service_id || s.service_name}-revenue`}
                        >
                          {money(s.revenue)}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
                <p className="mt-3 text-[10px] leading-tight text-muted-foreground/70">
                  Every closed treatment for this day. Static — click through Reports → Sales for a drill-down.
                </p>
              </CardContent>
            </Card>

            <Card className="rounded-lg" data-testid="packages-summary-card">
              <CardContent className="p-5">
                <div className="flex items-baseline justify-between mb-3">
                  <h2 className="tracking-overline text-[10px] text-muted-foreground">PACKAGES SUMMARY</h2>
                  <span className="text-[10px] text-muted-foreground uppercase tracking-wider">Qty · Revenue</span>
                </div>
                {loading ? (
                  <p className="text-xs text-muted-foreground py-4">Loading…</p>
                ) : !(data?.packages_summary || []).length ? (
                  <p className="text-xs text-muted-foreground py-4" data-testid="packages-summary-empty">
                    No packages sold on this day.
                  </p>
                ) : (
                  <ul className="divide-y divide-border/50">
                    {data.packages_summary.map((p) => (
                      <li
                        key={p.service_id || p.service_name}
                        className="flex items-center gap-3 py-1.5"
                        data-testid={`package-summary-${p.service_id || p.service_name}`}
                      >
                        <span className="uppercase text-xs font-medium truncate flex-1">{p.service_name}</span>
                        <span className="text-[11px] text-muted-foreground tabular-nums w-10 text-right">×{p.quantity}</span>
                        <span
                          className="text-xs text-muted-foreground whitespace-nowrap tabular-nums"
                          data-testid={`package-summary-${p.service_id || p.service_name}-revenue`}
                        >
                          {money(p.revenue)}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
                <p className="mt-3 text-[10px] leading-tight text-muted-foreground/70">
                  Every package sold on this day (net of refunds).
                </p>
              </CardContent>
            </Card>

            {/* Iter-267 — Daily-mode Cancellations card. Only rendered
                inside the Daily Mode branch so Trend Mode keeps its
                trend cards untouched. */}
            <Card className="rounded-lg md:col-span-2" data-testid="cancellations-card">
              <CardContent className="p-5">
                <div className="flex items-baseline justify-between mb-3">
                  <h2 className="tracking-overline text-[10px] text-muted-foreground">CANCELLATIONS</h2>
                  <span className="text-[10px] text-muted-foreground uppercase tracking-wider">Client · Service · Reason</span>
                </div>
                {loading ? (
                  <p className="text-xs text-muted-foreground py-4">Loading…</p>
                ) : !(data?.cancellations || []).length ? (
                  <p className="text-xs text-muted-foreground py-4" data-testid="cancellations-empty">
                    No cancellations or no-shows on this day.
                  </p>
                ) : (
                  <ul className="divide-y divide-border/50">
                    {data.cancellations.map((c, idx) => {
                      const isNoShow = c.status === "no_show";
                      // Iter-312 · cancellation label + reason derivation
                      // delegated to ReportingEngine (`deriveCancellationLabel`,
                      // `deriveCancellationReason`). Verbatim `reason` still
                      // wins; falls back to the category label when empty.
                      const reasonText = deriveCancellationReason(c);
                      const isPackage = !!c.is_package;
                      const engineLabel = deriveCancellationLabel(c);
                      const label = engineLabel || "\u2014";
                      return (
                        <li
                          key={`${c.customer_id || c.customer_name}-${c.package_id || c.start_iso || idx}`}
                          className="flex items-start gap-3 py-2"
                          data-testid={`cancellation-${c.customer_id || "walkin"}-${idx}`}
                        >
                          <span
                            className={
                              "inline-flex items-center px-1.5 rounded text-[10px] font-medium uppercase tracking-wider shrink-0 mt-0.5 " +
                              (isNoShow ? "bg-amber-100 text-amber-700" : "bg-muted text-muted-foreground")
                            }
                            data-testid={`cancellation-${idx}-status`}
                          >
                            {isNoShow ? "No-Show" : "Cancel"}
                          </span>
                          <span className="uppercase text-xs font-medium truncate w-40 shrink-0 mt-0.5">{c.customer_name}</span>
                          <span
                            className="text-xs text-muted-foreground truncate flex-1 shrink min-w-0 mt-0.5"
                            data-testid={`cancellation-${idx}-label`}
                            title={label}
                          >
                            {isPackage && (
                              <span className="inline-flex items-center px-1 mr-1 rounded bg-sky-50 text-sky-700 text-[9px] font-medium uppercase tracking-wider align-middle">
                                Package
                              </span>
                            )}
                            {label}
                          </span>
                          <span
                            className="text-[11px] text-muted-foreground italic break-words max-w-[280px] text-right leading-snug"
                            title={reasonText}
                            data-testid={`cancellation-${idx}-reason`}
                          >
                            {reasonText}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                )}
                <p className="mt-3 text-[10px] leading-tight text-muted-foreground/70">
                  Every cancelled or no-show appointment on this day. Packages collapse to a single row; the receptionist&rsquo;s verbatim note is shown when captured, otherwise the category label falls in.
                </p>
              </CardContent>
            </Card>
          </div>
        ) : (
        <div className="grid md:grid-cols-2 gap-4">
          <Card className="rounded-lg" data-testid="top-services-card">
            <CardContent className="p-5">
              <div className="flex items-baseline justify-between mb-3">
                <h2 className="tracking-overline text-[10px] text-muted-foreground">TOP SERVICES</h2>
                <SortToggle mode={svcSort} onChange={setSvcSort} testId="top-services-sort" />
              </div>
              {loading ? (
                <p className="text-xs text-muted-foreground py-4">Loading…</p>
              ) : sortedServices.length === 0 ? (
                <p className="text-xs text-muted-foreground py-4" data-testid="top-services-empty">No revenue yet in this range.</p>
              ) : (
                <ul className="space-y-2">
                  {sortedServices.map((s) => (
                    <li key={s.service_name} className="flex items-center gap-3" data-testid={`top-service-${s.service_id || s.service_name}`}>
                      <Star className="w-4 h-4 text-amber-400 shrink-0" fill="currentColor" />
                      {s.service_id ? (
                        <Link to={`/staff/brand/services?focus=${s.service_id}`} className="text-sky-600 hover:underline uppercase text-xs font-medium truncate flex-1">
                          {s.service_name}
                        </Link>
                      ) : (
                        <span className="uppercase text-xs font-medium truncate flex-1">{s.service_name}</span>
                      )}
                      <span className="text-xs text-muted-foreground whitespace-nowrap tabular-nums" data-testid={`top-service-${s.service_id || s.service_name}-value`}>
                        {svcSort === "quantity" ? `${s.quantity} performed` : money(s.revenue)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              <p
                className="mt-3 text-[10px] leading-tight text-muted-foreground/70"
                data-testid="top-services-caption"
              >
                Ranked from paid orders in this range. Refunds reduce revenue on the day they were issued.
              </p>
            </CardContent>
          </Card>

          <Card className="rounded-lg" data-testid="appts-by-day-card">
            <CardContent className="p-5">
              <div className="flex items-baseline justify-between mb-3">
                <h2 className="tracking-overline text-[10px] text-muted-foreground">APPOINTMENTS BY DAY OF WEEK</h2>
              </div>
              <div className="w-full h-64">
                <ResponsiveContainer>
                  <PieChart>
                    <Pie
                      data={pieData}
                      dataKey="count"
                      nameKey="day"
                      cx="45%"
                      cy="50%"
                      outerRadius={90}
                      label={({ pct }) => (pct > 0 ? `${pct}%` : "")}
                    >
                      {pieData.map((entry, i) => (
                        <Cell key={i} fill={DAY_COLOR_MAP[entry.day] || "#cbd5e1"} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v, n) => [v, n]} />
                    <Legend layout="vertical" verticalAlign="middle" align="right" iconType="circle" />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <p
                className="mt-1 text-[10px] leading-tight text-muted-foreground/70"
                data-testid="appts-by-day-caption"
              >
                Non-cancelled appointments grouped by their Bali-local weekday.
              </p>
            </CardContent>
          </Card>
          </div>
        )}

        {/* Bottom row: customers + new customers + total revenue */}
        <div className="grid md:grid-cols-2 gap-4">
          <Card className="rounded-lg" data-testid="top-care-team-card">
            <CardContent className="p-5">
              <div className="flex items-baseline justify-between mb-3">
                <h2 className="tracking-overline text-[10px] text-muted-foreground">MOST PERFORMING CARE TEAM</h2>
                <SortToggle mode={careSort} onChange={setCareSort} testId="top-care-team-sort" />
              </div>
              {loading ? (
                <p className="text-xs text-muted-foreground py-4">Loading…</p>
              ) : sortedCareTeam.length === 0 ? (
                <p className="text-xs text-muted-foreground py-4" data-testid="top-care-team-empty">No commission-assigned revenue yet in this range.</p>
              ) : (
                <ul className="space-y-2">
                  {sortedCareTeam.map((s) => (
                    <li key={s.staff_id} className="flex items-center gap-3" data-testid={`top-care-team-${s.staff_id}`}>
                      <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center shrink-0">
                        <UserIcon className="w-3.5 h-3.5 text-muted-foreground" />
                      </div>
                      <span className="uppercase text-xs font-medium truncate flex-1">{s.staff_name}</span>
                      <span className="text-xs text-muted-foreground whitespace-nowrap tabular-nums" data-testid={`top-care-team-${s.staff_id}-value`}>
                        {careSort === "quantity" ? `${s.quantity} clients` : money(s.revenue)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          <Card className="rounded-lg" data-testid="top-customers-card">
            <CardContent className="p-5">
              <div className="flex items-baseline justify-between mb-3">
                <h2 className="tracking-overline text-[10px] text-muted-foreground">TOP CUSTOMERS</h2>
                <div
                  className="flex items-center gap-1 text-[10px] uppercase tracking-wider"
                  data-testid="top-customers-sort"
                >
                  <button
                    type="button"
                    onClick={() => setCustSort("visits")}
                    className={`px-1 rounded transition-colors ${custSort === "visits" ? "text-primary font-semibold underline underline-offset-4" : "text-muted-foreground hover:text-foreground"}`}
                    data-testid="top-customers-sort-visits"
                    aria-pressed={custSort === "visits" ? "true" : "false"}
                  >By Visits</button>
                  <span className="text-muted-foreground">|</span>
                  <button
                    type="button"
                    onClick={() => setCustSort("spend")}
                    className={`px-1 rounded transition-colors ${custSort === "spend" ? "text-primary font-semibold underline underline-offset-4" : "text-muted-foreground hover:text-foreground"}`}
                    data-testid="top-customers-sort-spend"
                    aria-pressed={custSort === "spend" ? "true" : "false"}
                  >By Spend</button>
                </div>
              </div>
              {loading ? (
                <p className="text-xs text-muted-foreground py-4">Loading…</p>
              ) : sortedCustomers.length === 0 ? (
                <p className="text-xs text-muted-foreground py-4" data-testid="top-customers-empty">No paid orders yet in this range.</p>
              ) : (
                <ul className="space-y-2">
                  {sortedCustomers.map((c) => (
                    <li key={c.customer_id || c.customer_name} className="flex items-center gap-3" data-testid={`top-customer-${c.customer_id || "walkin"}`}>
                      <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center shrink-0">
                        <UserIcon className="w-3.5 h-3.5 text-muted-foreground" />
                      </div>
                      {c.customer_id ? (
                        <Link to={`/staff/brand/customers/${c.customer_id}`} className="text-sky-600 hover:underline uppercase text-xs font-medium truncate flex-1">
                          {c.customer_name}
                        </Link>
                      ) : (
                        <span className="uppercase text-xs font-medium truncate flex-1">{c.customer_name}</span>
                      )}
                      <span className="text-xs text-muted-foreground whitespace-nowrap tabular-nums" data-testid={`top-customer-${c.customer_id || "walkin"}-value`}>
                        {custSort === "visits" ? `${c.visits || 0} visits` : money(c.spend)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          {/* Iter-282 — Standard Mode now surfaces both New Guests
              and Total Revenue as full-width hero cards at the TOP
              of the page (Row 1 + Row 2), so the legacy
              bottom-right stacked column has been removed. Daily
              Mode is unchanged; its own top-of-page hero
              (Iter-266) already replaced the legacy column. */}
        </div>
      </div>
    </StaffShell>
  );
}